import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import { useStores } from 'stores/StoreContextProvider';
import routeDefinitions from 'enum/routeDefinitions';
import Button from 'components/Button/Button';
import {
  actionCreators as appStateActionCreators,
  selector as appSelector,
} from 'app/AppState';
import { actionCreators as paymentActionCreators } from 'features/Payment/State';
import { RegistrationModalCaller } from 'pages/RegistrationModal/utils';

import locked from '../../../images/locked.png';

import pingPopup from './pingPopup';
import styles from './SFWOverlay.css';


const vmaPaths = [
  routeDefinitions.verifyMyAge,
  routeDefinitions.verifyMyAge.toLowerCase(),
];

const vmaStyle = {
  height: 720,
  width: 480,
  left: () => (window.screen.width / 2) - (vmaStyle.height / 2),
  top: () => (window.screen.height / 2) - (vmaStyle.width / 2),
};

function SFWOverlay(props) {
  const {
    realCountry,
    appActions,
    paymentActions,
    portrait,
    isMobile,
  } = props;

  const { setShowRegistrationModal } = appActions;
  const { showBuyTokensModal } = paymentActions;
  const { UserStore } = useStores();
  const { user } = UserStore;
  const { username, sfw, sfwDetails } = user;

  const openRegModal = useCallback(() => {
    setShowRegistrationModal(true, RegistrationModalCaller.SFW_OVERLAY);
  }, [setShowRegistrationModal]);

  const onClickOpenLoginModal = useCallback(() => {
    ModalService.showModal(
      ModalService.MODAL_KEYS.LOGIN_MODAL_V2,
      { handleOpenRegistrationModal: openRegModal }
    );
  }, [openRegModal]);

  const onMessage = useCallback((event) => {
    const { data, source } = event;
    const { pathname: eventPath, origin: eventOrigin } = source.location;
    const { origin } = window.location;

    if (eventOrigin !== origin) {
      return;
    }

    if (!vmaPaths.includes(eventPath)) {
      return;
    }

    if (data.verified) {
      window.location.reload();
    }

    window.removeEventListener('message', onMessage);
    pingPopup.stopPing();
  }, []);

  const onOpenVerifyMyAgePopup = useCallback(() => {
    window.addEventListener('message', onMessage, false);

    const styleConfigs = [
      'resizable=no',
      `height=${vmaStyle.height}`,
      `width=${vmaStyle.width}`,
      `left=${vmaStyle.left()}`,
      `top=${vmaStyle.top()}`,
    ].join(',');

    const { vmaUrl } = sfwDetails;
    const popup = window.open(vmaUrl, '', styleConfigs);

    const paths = [
      `${window.location.origin}${vmaPaths[0]}`,
      `${window.location.origin}${vmaPaths[1]}`,
    ];

    pingPopup.startPing(popup, paths);
  }, [sfwDetails, onMessage]);

  const onClickShowVerifyModal = useCallback(() => {
    if (sfw && sfwDetails) {
      onOpenVerifyMyAgePopup();
    } else {
      UserStore.showSFWAgeVerificationModal = true;
      showBuyTokensModal();
    }
  }, [
    UserStore,
    showBuyTokensModal,
    sfw,
    sfwDetails,
    onOpenVerifyMyAgePopup,
  ]);

  const containerClasses = classNames({
    [styles.container]: true,
    [styles.portraitMobile]: portrait && isMobile,
  });

  let translatedCountry = undefined;
  let verifyBtnLabel = undefined;
  if (realCountry) {
    verifyBtnLabel = translate('strings.sfw.get.verified.now');
    translatedCountry = translate(`countries.${realCountry}`);
  } else {
    verifyBtnLabel = translate('strings.sfw.get.verified');
  }

  return (
    <div className={containerClasses}>
      <img alt="" src={locked} />
      {realCountry ? (
        <div>
          {translate(
            'strings.sfw.verify.content.age',
            { 0: translatedCountry }
          )}
        </div>
      ) : (
        <Fragment>
          <h3>{translate('strings.sfw.unlock.content')}</h3>
          <div>{translate('strings.sfw.verify.content.access')}</div>
        </Fragment>
      )}
      <div>
        <Button
          colour="orange"
          label={verifyBtnLabel}
          onClick={onClickShowVerifyModal}
        />
        {!username && (
          <Button
            colour="orange"
            label={translate('login.login.submit')}
            onClick={onClickOpenLoginModal}
          />
        )}
      </div>
    </div>
  );
}

SFWOverlay.propTypes = {
  appActions: PropTypes.object.isRequired,
  paymentActions: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
  portrait: PropTypes.bool,
  realCountry: PropTypes.string,
};

SFWOverlay.defaultProps = {
  isMobile: false,
  portrait: false,
  realCountry: undefined,
};

export default connect(appSelector, (dispatch) => {
  return {
    appActions: bindActionCreators(appStateActionCreators, dispatch),
    paymentActions: bindActionCreators(paymentActionCreators, dispatch),
  };
})(SFWOverlay);
